import { Chip, StatusPill } from "@/components/iris";
import { LogToolbar } from "@/features/logs/log-toolbar";
import { useLogs } from "@/hooks/use-logs";
import type { Env } from "@/lib/types";
import { useState } from "react";

export function LogsTab() {
  const [env, setEnv] = useState<Env>("dev");
  const [level, setLevel] = useState("");
  const [source, setSource] = useState("");
  const [search, setSearch] = useState("");

  const { data, isLoading } = useLogs({
    ...(level ? { level } : {}),
    ...(source ? { source } : {}),
    ...(search ? { q: search } : {}),
    env,
    limit: 100,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--iris-s-4)" }}>
      {/* Header row: env switcher */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <p className="iris-meta">
          Showing logs from the{" "}
          <span style={{ fontWeight: 600, color: "var(--iris-fg-1)" }}>{env}</span> environment
        </p>
        <div style={{ display: "flex", gap: 6 }}>
          <Chip active={env === "dev"} onClick={() => setEnv("dev")}>
            Dev
          </Chip>
          <Chip active={env === "prod"} onClick={() => setEnv("prod")}>
            Prod
          </Chip>
        </div>
      </div>

      {/* Toolbar */}
      <LogToolbar
        level={level}
        source={source}
        search={search}
        onLevelChange={setLevel}
        onSourceChange={setSource}
        onSearchChange={setSearch}
      />

      {/* Summary pills */}
      {data && (
        <div style={{ display: "flex", gap: 10 }}>
          <StatusPill variant="err">{data.errors} errors</StatusPill>
          <StatusPill variant="warn">{data.warnings} warnings</StatusPill>
          <StatusPill variant="idle">{data.total} total</StatusPill>
        </div>
      )}

      {/* Log lines */}
      <div className="iris-card" style={{ overflow: "hidden" }}>
        <table className="iris-table">
          <thead>
            <tr>
              <th style={{ width: 150 }}>Timestamp</th>
              <th style={{ width: 60 }}>Level</th>
              <th style={{ width: 140 }}>Source</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={4}
                  style={{ textAlign: "center", color: "var(--iris-fg-4)", padding: "16px 12px" }}
                >
                  Loading...
                </td>
              </tr>
            ) : (data?.events ?? []).length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  style={{ textAlign: "center", color: "var(--iris-fg-4)", padding: "16px 12px" }}
                >
                  No log events
                </td>
              </tr>
            ) : (
              (data?.events ?? []).map((event, i) => (
                <tr key={`${event.timestamp}-${i}`}>
                  <td className="iris-td--mono" style={{ color: "var(--iris-fg-5)" }}>
                    {event.timestamp}
                  </td>
                  <td
                    style={{
                      fontWeight:
                        event.level === "ERROR" || event.level === "WARN" ? 600 : undefined,
                      color:
                        event.level === "ERROR"
                          ? "var(--iris-neon-pink)"
                          : event.level === "WARN"
                            ? "var(--iris-neon-orange)"
                            : "var(--iris-fg-4)",
                    }}
                  >
                    {event.level}
                  </td>
                  <td className="iris-td--muted">{event.source}</td>
                  <td
                    style={{ fontFamily: "var(--iris-font-mono)", fontSize: "var(--iris-t-small)" }}
                  >
                    {event.message}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
